import { ColumnState, SortChangedEvent } from '@ag-grid-community/core'
import { AgGridReact } from '@ag-grid-community/react'
import dayjs, { Dayjs } from 'dayjs'
import { uniqBy } from 'lodash'
import React, {
  forwardRef,
  PropsWithChildren,
  RefAttributes,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { ICommonObject } from 'src/app/shared/models/common.model'
import { getUserProfile } from 'src/app/store/auth'
import { getCriticalConfig, getDeferredConfig } from 'src/app/store/masterSetup'
import { colorBgBrandless, colorText } from 'src/app/themes/primaryThemeDefault'
import {
  IColumnSetting,
  IDateFilterProps,
  IGetColumnSettingPayload,
  IGetExportData,
  SortDirect,
} from '../table-setting.model' 
import {
  defaultActionColId,
  defaultActionColumnMaxWidth,
  defaultColumnDefs,
  defaultColumnMinWidth,
  defaultOptions,
  defaultPageSize,
  defaultPagination,
  defaultSearch,
  defaultSearchData,
} from '../table.constants'
import { useUserSetting } from '../table.hook'
import { IOuterTableRef, ISearchValue, ITableContext, TableColumnDefs, TableProps } from '../table.model'
import TableActionColumn from './TableActionColumn'

export const outerTableRef = React.createRef<Partial<IOuterTableRef>>()
export const providerRef = React.createRef<IOuterTableRef>()
export const tableRef = React.createRef<AgGridReact<any>>()

const TableContext = React.createContext<ITableContext>({
  tableRef,
} as any)

export const useTableContext = () => useContext(TableContext)

const TableProvider: React.FC<RefAttributes<IOuterTableRef> & PropsWithChildren<TableProps>> = forwardRef<
  IOuterTableRef,
  PropsWithChildren<TableProps>
>((props, ref) => {
  const {
    children,
    displayActionColumn = true,
    isGettingList,
    loading,
    suppressLoadingOverlay = false,
    total = 0,
    rowData,
    tableHeaderBackgroundColor = colorBgBrandless,
    tableHeaderFontColor = colorText,
    disableTableControl,
    translationNamespace,
    suppressNoRowsOverlay = true,
    tableSettingName = '',
    defaultParams = {},
    handleSearch,
    onSortChanged,
    generateCustomColumnDefs,
    onGridReady,
    isSearchDropdown = false,
    resetViewCallback,
    settingHeightTable,
    ...etc
  } = props

  const { userSetting, isGettingSetting, isUpdatingSetting, getSetting, updateSetting, setUserSetting } =
    useUserSetting()
  const { t } = useTranslation()
  const userProfile = useSelector(getUserProfile)
  const _loading = useMemo(
    () => loading || isUpdatingSetting || isGettingSetting || isGettingList,
    [isGettingList, isGettingSetting, isUpdatingSetting, loading],
  )
  const criticalConfig = useSelector(getCriticalConfig)
  const majorConfig = useSelector(getDeferredConfig)
  // const [originalWidth, setOriginalWidth] = useState<Array<ColumnOriginalWidth>>([])

  const filterOutActionColumn = useCallback((arr: Array<TableColumnDefs>): Array<TableColumnDefs> => {
    return arr?.filter((item) => !item?.hide && !item?.rowGroup && ![defaultActionColId].includes(item?.colId))
  }, [])

  const getHeaderName = useCallback(
    (field: string): string => {
      return t(`${translationNamespace}.${field}`)
    },
    [t, translationNamespace],
  )

  const checkIsUserChangedSetting = useCallback((userSetting: IColumnSetting[]) => {
    return userSetting.filter((item) => item.defaultSort).some((item) => item.isSelect !== item.defaultSelect)
  }, [])

  const storageKey = `grid-sort-${tableSettingName ?? 'default'}`

  const getSortColumns = useCallback(
    (res: IColumnSetting[]) => {
      const isUserChangedSetting = checkIsUserChangedSetting(res)
      const displayedColumns = res.filter((item) => item.isSelect)
      const defaultSortColumns = res.filter((item) => item.defaultSort)
      const sortColumnsDictionary = isUserChangedSetting
        ? {
          [displayedColumns[0].propertyName]: SortDirect.Asc,
        }
        : Object.assign({}, ...defaultSortColumns.map((key) => ({ [key.propertyName]: key.sortDirection })))
      return sortColumnsDictionary
    },
    [checkIsUserChangedSetting],
  )

  const mergeSortStateWithLocalStorage = useCallback(
    (res: IColumnSetting[]) => {
      let sortColumnsDictionary = getSortColumns(res)
      let validSortState: ColumnState[] | null = null

      const savedSort = localStorage.getItem(storageKey)

      if (savedSort && savedSort !== '[]') {
        try {
          const sortState: ColumnState[] = JSON.parse(savedSort)
          const visibleColumns = res.filter((col) => col.isSelect).map((col) => col.propertyName)

          validSortState = sortState.filter((col) => visibleColumns.includes(col.colId))

          if (validSortState.length > 0) {
            sortColumnsDictionary = validSortState.reduce((acc, col) => {
              if (col.sort) acc[col.colId] = col.sort
              return acc
            }, {} as Record<string, string>)
          } else {
            localStorage.removeItem(storageKey)
            validSortState = null
          }
        } catch (error) {
          console.error('Error parsing localStorage sort state:', error)
          localStorage.removeItem(storageKey)
          validSortState = null
        }
      }

      return { sortColumnsDictionary, validSortState }
    },
    [getSortColumns, storageKey],
  )

  const applyVisualSortState = useCallback((validSortState: ColumnState[]) => {
    const api = tableRef.current?.api
    if (!api) return

    const currentState = api.getColumnState()

    const sortMap = validSortState.reduce((acc, col) => {
      acc[col.colId] = { sort: col.sort, sortIndex: col.sortIndex }
      return acc
    }, {} as Record<string, { sort: 'asc' | 'desc' | null | undefined; sortIndex: number | null | undefined }>)

    const newState: ColumnState[] = currentState.map((col) => ({
      ...col,
      sort: sortMap[col.colId]?.sort ?? null,
      sortIndex: sortMap[col.colId]?.sortIndex ?? null,
    }))

    api.applyColumnState({
      state: newState,
      applyOrder: false,
    })
  }, [])

  const getSelectedRows = useCallback((): Array<ICommonObject> => {
    return tableRef.current?.api?.getSelectedRows() ?? []
  }, [])

  const convertUserSettingToColDefs = useCallback(
    (userSetting: IColumnSetting[]): TableColumnDefs[] => {
      // default setting on load
      // check if user changed default column display
      // if changed, sort followed by first column (first displayed columns)
      // if not changed, sort followed by default
      const isUserChanged = checkIsUserChangedSetting(userSetting)
      const displayedColumns = userSetting.filter((item) => item.isSelect)
      const data: TableColumnDefs[] = userSetting.map((item, index) => {
        const displayedFirstColumnIndex = displayedColumns.findIndex((i) => i.propertyName === item.propertyName)
        return {
          field: item.propertyName,
          headerName: getHeaderName(item.propertyName),
          hide: !item.isSelect,
          colId: item.propertyName,
          defaultSelect: item.defaultSelect,
          sortIndex: isUserChanged
            ? displayedFirstColumnIndex === 0
              ? 0
              : null
            : item.defaultSort
              ? index
              : undefined,
          sort: isUserChanged ? (displayedFirstColumnIndex === 0 ? SortDirect.Asc : null) : item.sortDirection,
          sortingOrder: ['asc', 'desc', null],
        }
      })

      return generateCustomColumnDefs ? generateCustomColumnDefs(data) : data
    },
    [checkIsUserChangedSetting, generateCustomColumnDefs, getHeaderName],
  )

  const defs = useMemo<Array<TableColumnDefs>>(() => {
    const formattedCols = convertUserSettingToColDefs(userSetting.sort((a, b) => a.displayOrder - b.displayOrder))
    const cols = uniqBy(
      formattedCols.map((item, index) => {
        const data: TableColumnDefs = {
          ...item,
          ...defaultColumnDefs,
          minWidth: item.minWidth ?? defaultColumnMinWidth,
          sortable: [defaultActionColId].includes(item.colId) ? false : item.sortable,
          flex: item.flex ?? 1,
        }
        return data
      }),
      'colId',
    )

    return displayActionColumn
      ? [
        {
          colId: defaultActionColId,
          headerName: '',
          field: '',
          hide: false,
          defaultSelect: true,
          pinned: 'left',
          minWidth: defaultActionColumnMaxWidth,
          maxWidth: defaultActionColumnMaxWidth,
          sortable: false,
          resizable: false,
          cellRenderer: (props: any) => {
            return props?.data?.isHideMenuAction ? '' : <TableActionColumn<typeof props.data> data={props.data} />
          },
        },
        ...cols,
      ]
      : cols
  }, [displayActionColumn, userSetting, convertUserSettingToColDefs])

  const getTableState = useCallback(() => {
    return tableRef.current?.api?.getColumnState() ?? []
  }, [])

  // table search state
  const [searchOpen, setSearchOpen] = useState<boolean>(false)
  const form = useForm({
    defaultValues: defaultSearch,
  })
  const { control, reset, resetField } = useMemo(() => form, [form])

  const searchColumnValue = useWatch({
    control,
    name: 'searchColumns',
  })
  const searchTextvalue = useWatch({
    control,
    name: 'searchTerm',
  })

  // table sort state
  const [sortColumnsDictionary, setSortColumnDictionary] = useState<ICommonObject>({})

  //table search data state
  const [tableSearchData, setTableSearchData] = useState<Partial<IGetExportData>>(defaultSearchData)

  // table dropdown states
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false)
  const [chosenColumns, setChosenColumn] = useState<Array<string>>([]) // state for displaying checkbox
  const [isSearch, setIsSearch] = useState<boolean>(false)

  // date filter states
  const [dateFilter, setDateFilter] = useState<IDateFilterProps[]>([])

  // common functions for searching
  const getChosenColumns = useCallback(
    (values: { column: string; text: string }) => {
      return values.column !== defaultOptions[0].value
        ? [values.column]
        : filterOutActionColumn(defs).map((item) => item.colId)
    },
    [defs, filterOutActionColumn],
  )

  // table pagination states
  const recordsPerPage = useMemo(
    () => majorConfig?.systemSettings.recordsPerPage ?? defaultPageSize,
    [majorConfig?.systemSettings.recordsPerPage],
  )
  const [currentPage, setCurrentPage] = useState<number>(defaultPagination)
  const [pageSize, setPageSize] = useState<number>(recordsPerPage)
  const totalRows = useMemo(() => (rowData && rowData?.length === 0 ? 0 : total), [rowData, total])

  const handleSearchLogic = useCallback(
    async (value: Partial<IGetExportData>) => {
      const chosenColumns = getChosenColumns({
        column: searchColumnValue,
        text: searchTextvalue,
      })
      const searchData: Partial<IGetExportData> = {
        pageIndex: currentPage,
        pageSize: pageSize,
        searchColumns: chosenColumns,
        searchTerm: searchTextvalue,
        sortColumnsDictionary,
        ...value,
      }
      if (dateFilter.length > 0) {
        searchData.dateFilter = dateFilter
      }
      await handleSearch(searchData)
      setTableSearchData(searchData)
      tableRef.current?.api.clearFocusedCell()
      tableRef.current?.api.clearRangeSelection()
    },
    [
      currentPage,
      dateFilter,
      getChosenColumns,
      handleSearch,
      pageSize,
      searchColumnValue,
      searchTextvalue,
      sortColumnsDictionary,
    ],
  )

  // table search methods
  const onSearch = useCallback(
    async (_: ISearchValue) => {
      setIsSearch(true)
      setCurrentPage(1)
      await handleSearchLogic({
        pageIndex: 1,
      })
    },
    [handleSearchLogic],
  )

  // table dropdown methods
  const onApplyColumn = useCallback(
    async (originalDefs?: TableColumnDefs[], reloadFilter: boolean = true) => {
      const data = {
        userId: userProfile.userId,
        tableName: tableSettingName,
        items: originalDefs
          ? originalDefs
            .filter((item) => item.colId !== defaultActionColId)
            .map((item) => ({
              propertyName: item.field,
              isSelect: !item.hide,
            }))
          : defs
            .filter((item) => item.colId !== defaultActionColId)
            .map((item) => ({
              propertyName: item.field,
              isSelect: chosenColumns.includes(item.colId),
            })),
      }
      const res = await updateSetting(data)
      if (reloadFilter) {
        const { sortColumnsDictionary, validSortState } = mergeSortStateWithLocalStorage(res)
        await handleSearchLogic({
          sortColumnsDictionary,
        })
        setSortColumnDictionary(sortColumnsDictionary)

        if (validSortState && validSortState.length > 0) {
          applyVisualSortState(validSortState)
        }
      }
      setDropdownOpen(false)
    },
    [chosenColumns, defs, tableSettingName, userProfile.userId, mergeSortStateWithLocalStorage, applyVisualSortState, handleSearchLogic, updateSetting],
  )

  const onCancel = useCallback(() => {
    setChosenColumn(filterOutActionColumn(defs).map((item) => item.colId))
    setDropdownOpen(false)
  }, [defs, filterOutActionColumn])

  const onToggleDropdown = useCallback(() => {
    const newOpen = !dropdownOpen
    // save state when open for canceling/closing dropdown
    if (!newOpen) {
      setChosenColumn(filterOutActionColumn(defs).map((item) => item.colId))
    }
    setDropdownOpen(newOpen)
  }, [defs, dropdownOpen, filterOutActionColumn])

  // table pagination methods
  const setCurrent = useCallback(
    async (page: number, pageSize: number) => {
      setCurrentPage(page)
      setPageSize(pageSize)
      await handleSearchLogic({
        pageIndex: page,
        pageSize,
      })
    },
    [handleSearchLogic],
  )

  const onJumpToFirst = useCallback(async () => {
    await setCurrent(defaultPagination, pageSize)
  }, [pageSize, setCurrent])

  const onJumpToLast = useCallback(async () => {
    const lastPage = Math.ceil(totalRows / pageSize)
    await setCurrent(lastPage, pageSize)
  }, [pageSize, setCurrent, totalRows])

  const onPaginationChange = useCallback(
    async (page: number, pageSize: number) => {
      await setCurrent(page, pageSize)
    },
    [setCurrent],
  )

  const onPageSizeChange = useCallback(
    async (size: number) => {
      await setCurrent(defaultPagination, size)
    },
    [setCurrent],
  )

  // date filter methods
  const handleSetDateFilter = useCallback(
    (dateType: string, range: [start: Dayjs | null, end: Dayjs | null]) => {
      const index = dateFilter.findIndex((item) => item.dateType === dateType)
      const dateFormat = 'YYYY-MM-DD'
      if (!range) {
        // unselect date
        setDateFilter((prev) => [...prev.filter((item) => item.dateType !== dateType)])
      } else {
        // when dates are selected
        const dateFrom = range[0] ? dayjs(range[0]).format(dateFormat) : null
        const dateTo = range[1] ? dayjs(range[1]).format(dateFormat) : null
        if (index < 0) {
          setDateFilter((prev) => [
            ...prev,
            {
              dateType,
              dateFrom,
              dateTo,
            },
          ])
        } else {
          const newArr = JSON.parse(JSON.stringify(dateFilter ?? []))
          newArr[index].dateFrom = dateFrom
          newArr[index].dateTo = dateTo
          setDateFilter(JSON.parse(JSON.stringify(newArr)))
        }
      }
    },
    [dateFilter],
  )

  const resetDateFilter = useCallback(() => setDateFilter([]), [])

  // reset button methods
  const onResetView = useCallback(
    async (searchPayload?: ICommonObject) => {
      const newDefs: Array<TableColumnDefs> =
        defs?.map((item: any, index) => ({
          ...item,
          ...defaultColumnDefs,
          hide: !userSetting.find((col) => col.propertyName === item.colId)?.defaultSelect,
        })) ?? []
      const sortColumnsDictionary = Object.assign(
        {},
        ...userSetting.filter((item) => item.defaultSort).map((key) => ({ [key.propertyName]: key.sortDirection })),
      )

      resetViewCallback?.()

      await onApplyColumn(newDefs, false)
      await handleSearchLogic({
        pageIndex: defaultPagination,
        pageSize: recordsPerPage,
        searchColumns: userSetting.map((item) => item.propertyName),
        searchTerm: defaultSearch.searchTerm,
        sortColumnsDictionary,
        ...searchPayload,
      })

      // reset search field
      reset()
      setSearchOpen(false)

      // reset dropdown
      setDropdownOpen(false)
      setChosenColumn(filterOutActionColumn(newDefs).map((item) => item.colId))

      // reset pagination
      setPageSize(recordsPerPage)
      setCurrentPage(defaultPagination)

      // reset column width
      // tableRef.current?.api.setColumnWidths(originalWidth)

      // reset sort columns
      setSortColumnDictionary(sortColumnsDictionary)
    },
    [
      defs,
      onApplyColumn,
      handleSearchLogic,
      recordsPerPage,
      userSetting,
      reset,
      filterOutActionColumn,
      resetViewCallback,
    ],
  )

  // sort change
  const _onSortChanged = useCallback(
  async (event: SortChangedEvent<any, any>) => {
    const api = event.api;

    if (event?.source === 'uiColumnSorted') {
      const state = api.getColumnState();

      // Save only columns that have sort applied
      const sortState = state.filter(
        col => col.sort === 'asc' || col.sort === 'desc'
      );

      localStorage.setItem(storageKey, JSON.stringify(sortState));

      // Extract the primary sorted column (with sortIndex === 0) to send to backend
      const primarySort = state.find(col => col.sortIndex === 0);

      const sortData = {
        [primarySort?.colId ?? '']: primarySort?.sort,
      };

      await handleSearchLogic({
        sortColumnsDictionary: sortData,
      });

      setSortColumnDictionary(sortData);
    }
  },
  [handleSearchLogic, storageKey]
);


  // width
  const onColumnResized = useCallback(() => {
    const state = getTableState().map((item) => ({ ...item, maxWidth: undefined })) as any
    const updatedDefs = defs.map((item, index) => ({ ...item, ...state[index] }))
    tableRef.current?.api.updateGridOptions({
      columnDefs: updatedDefs,
    })
  }, [defs, getTableState])

  // get column width
  // const getColumnWidth = useCallback((): Array<ColumnOriginalWidth> => {
  //   const state = getTableState()
  //   return state.map((item) => ({ key: item.colId, newWidth: item.width ?? 0 }))
  // }, [getTableState])

  // on grid ready
  const handleGridReady = useCallback(
    async () => {
      if (userProfile?.userId && tableSettingName) {
        const payload: IGetColumnSettingPayload = {
          userId: userProfile.userId,
          tableName: tableSettingName,
        }

        const res = await getSetting(payload)

        const { sortColumnsDictionary, validSortState } = mergeSortStateWithLocalStorage(res)

        const searchData: Partial<IGetExportData> = {
          searchTerm: '',
          searchColumns: res.map((item) => item.propertyName),
          sortColumnsDictionary,
          pageSize: defaultPageSize,
          pageIndex: defaultPagination,
          ...defaultParams,
        }

        await handleSearchLogic(searchData)
        setSortColumnDictionary(sortColumnsDictionary)

        if (validSortState && validSortState.length > 0) {
          applyVisualSortState(validSortState)
        }
      }
    },
    [
      defaultParams,
      getSetting,
      handleSearchLogic,
      mergeSortStateWithLocalStorage,
      applyVisualSortState,
      tableSettingName,
      userProfile?.userId,
    ],
  )



  useEffect(() => {
    const api = tableRef.current?.api
    if (!api) return

    if (_loading) {
      api.setGridOption('suppressNoRowsOverlay', true)
      if (suppressLoadingOverlay) {
        api.hideOverlay()
      } else {
        api.showLoadingOverlay()
      }
      return
    }

    if (suppressNoRowsOverlay && (!rowData?.length || rowData.length === 0)) {
      api.setGridOption('suppressNoRowsOverlay', false)
      api.showNoRowsOverlay()
      return
    }

    api.setGridOption('suppressNoRowsOverlay', true)
    api.hideOverlay()
  }, [_loading, rowData, suppressNoRowsOverlay, suppressLoadingOverlay])

  useEffect(() => {
    if (isSearchDropdown) {
      resetField('searchTerm')
    }
  }, [isSearchDropdown])

  useEffect(() => {
    setChosenColumn(filterOutActionColumn(defs)?.map((item) => item.colId))
  }, [defs, filterOutActionColumn, setChosenColumn])

  useEffect(() => {
    if (disableTableControl) {
      const state = getTableState().map((item) => ({ ...item, sortable: false, resizable: false }))
      tableRef.current?.api?.applyColumnState({
        state,
      })
    }
  }, [disableTableControl, getTableState])

  const setSearchFormValues = useCallback((searchTerm: string, searchColumns: any) => {
    form.setValue('searchTerm', searchTerm, { shouldValidate: true, shouldDirty: true, shouldTouch: true })
    form.setValue('searchColumns', searchColumns, { shouldValidate: true, shouldDirty: true, shouldTouch: true })
  }, [form])

  const setChosenColumnsHandler = useCallback((columns: Array<string>) => {
    setChosenColumn(columns)
  }, [])

  const getChosenColumnsHandler = useCallback(() => {
    return chosenColumns
  }, [chosenColumns])

  const getSearchFormValues = useCallback(() => {
    return {
      searchTerm: searchTextvalue,
      searchColumns: searchColumnValue,
    }
  }, [searchTextvalue, searchColumnValue])

  useImperativeHandle(ref, () => ({
    onGridReady: handleGridReady,
    setTableSearchData,
    getSetting,
    handleSearchLogic,
    setUserSetting,
    getSortColumns,
    setSortColumnDictionary,
    setCurrentPage,
    onResetView,
    getSelectedRows,
    setSearchFormValues,
    setChosenColumns: setChosenColumnsHandler,
    getChosenColumns: getChosenColumnsHandler,
    getSearchFormValues,
    setSearchOpen,
  }))

  return (
    <TableContext.Provider
      value={{
        defs,
        tableRef,
        settingHeightTable,
        tableHeaderBackgroundColor:
          criticalConfig?.systemColor.tableHeaderBackgroundColor ?? tableHeaderBackgroundColor,
        tableHeaderFontColor: criticalConfig?.systemColor.tableHeaderFontColor ?? tableHeaderFontColor,
        tableSearchData,
        searchOpen,
        form,
        displayActionColumn,
        total: totalRows,
        rowData,
        chosenColumns,
        isUpdatingSetting,
        isGettingList,
        isGettingSetting,
        currentPage,
        pageSize,
        dropdownOpen,
        disableTableControl,
        searchTerm: searchTextvalue,
        tableSettingName,
        loading: _loading,
        dateFilter,
        resetDateFilter,
        handleSetDateFilter,
        setChosenColumn,
        setSearchOpen,
        handleSearch,
        setDateFilter,
        onColumnResized,
        onSortChanged: _onSortChanged,
        onResetView,
        onPageSizeChange,
        onPaginationChange,
        onJumpToFirst,
        onJumpToLast,
        onToggleDropdown,
        onCancel,
        onApplyColumn,
        onSearch,
        onGridReady: handleGridReady,
        getHeaderName,
        isSearch,
        suppressNoRowsOverlay,
        isSearchDropdown,
        ...etc,
      }}
    >
      {children}
    </TableContext.Provider>
  )
})

export default TableProvider
